import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Offcanvas, OffcanvasHeader, OffcanvasBody } from "reactstrap";
import { Button } from "./Button";
import { Dialog } from "./Dialog";

/**
 * 右側詳情抽屜（design system primitive，模板 Ui > UiOffcanvas 的 Offcanvas）。
 *
 * 用於「看一眼就走」的快速檢視(監控詳情、列表快速預覽),
 * 需要完整欄位與審核歷程的請用 <DetailView> 詳情頁。
 *
 *   <Drawer isOpen={!!row} title="監控詳情" onClose={close} actions={<Button>前往派工</Button>}>
 *     ...
 *   </Drawer>
 *
 * dirty=true(裡面有沒存的輸入)時,關閉會先問過再關 —— 不會默默丟掉使用者打的字。
 */
export const Drawer = ({
  isOpen, title, onClose, actions, children,
  direction = "end", dirty = false, width, className,
}) => {
  const [asking, setAsking] = useState(false);

  // 抽屜被外部關掉(例如切換選取的資料)時,別留一個孤兒確認框在畫面上
  useEffect(() => { if (!isOpen) setAsking(false); }, [isOpen]);

  const requestClose = () => {
    if (dirty) { setAsking(true); return; }
    if (onClose) onClose();
  };
  const discard = () => { setAsking(false); if (onClose) onClose(); };

  return (
    <>
      <Offcanvas
        isOpen={isOpen}
        toggle={requestClose}
        direction={direction}
        className={className}
        style={width ? { width } : undefined}
      >
        <OffcanvasHeader toggle={requestClose} close={
          <Button variant="link" className="p-0 text-reset" aria-label="關閉抽屜" onClick={requestClose}>
            <i className="bx bxs-x-circle" style={{ fontSize: 24 }} />
          </Button>
        }>
          {title}
        </OffcanvasHeader>
        <OffcanvasBody className="d-flex flex-column">
          <div className="flex-grow-1">{children}</div>
          {actions ? <div className="d-flex justify-content-end gap-2 pt-3">{actions}</div> : null}
        </OffcanvasBody>
      </Offcanvas>

      <Dialog
        isOpen={asking}
        toggle={() => setAsking(false)}
        title="關閉前確認"
        confirmText="捨棄變更並關閉"
        confirmVariant="danger"
        onConfirm={discard}
      >
        尚有未儲存的變更,關閉後不會保留。確定要關閉嗎？
      </Dialog>
    </>
  );
};
Drawer.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.node,
  onClose: PropTypes.func,
  actions: PropTypes.node,
  children: PropTypes.node,
  direction: PropTypes.oneOf(["start", "end", "top", "bottom"]),
  // 內容有未儲存的輸入 → 關閉前先確認
  dirty: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // 需要頁面級外觀(如監控詳情的深色標題列)時掛在 Offcanvas 上,樣式寫在 _dck-figma.scss
  className: PropTypes.string,
};

export default Drawer;
